import { useState } from "react";
import { IoMdHelpCircle } from "react-icons/io";
import { MdClose, MdNavigateBefore, MdNavigateNext } from "react-icons/md";

// Botão "?" + popup de ajuda para telas de cadastro/movimentação.
// O conteúdo é dividido em passos (intro > passo a passo > uma página por
// seção > dicas), navegados com Anterior/Próximo e uma transição leve —
// evita jogar um texto gigante de uma vez só na tela.
//
// Uso (tela de cadastro, com campos obrigatórios/opcionais):
// <AjudaTela
//   titulo="Cadastro de Fornecedores"
//   descricao="Explicação geral do que essa tela faz e quando usá-la."
//   secoes={[
//     { titulo: "Dados Principais", campos: [
//       { nome: "Razão Social", obrigatorio: true, explicacao: "Nome oficial da empresa, como consta no CNPJ." },
//     ]},
//   ]}
//   dicas={["Dica opcional exibida no final do popup."]}
// />
//
// Uso (tela operacional, sem campos de formulário — um roteiro numerado):
// <AjudaTela
//   titulo="Pesagem no Brete"
//   descricao="Explicação geral."
//   passos={[
//     { titulo: "1. Leia o brinco", texto: "Passe o bastão no brinco do animal." },
//     "Um passo também pode ser só uma string, sem título.",
//   ]}
// />
export default function AjudaTela({
  titulo,
  descricao,
  passos = [],
  secoes = [],
  dicas = [],
}) {
  const [aberto, setAberto] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [visivel, setVisivel] = useState(true);

  const paginas = [
    ...(descricao ? [{ tipo: "intro", texto: descricao }] : []),
    ...(passos.length > 0 ? [{ tipo: "passos", itens: passos }] : []),
    ...secoes.map((secao) => ({ tipo: "secao", ...secao })),
    ...(dicas.length > 0 ? [{ tipo: "dicas", itens: dicas }] : []),
  ];

  const totalPaginas = paginas.length;
  const atual = paginas[pagina] || paginas[0];
  const primeiraPagina = pagina === 0;
  const ultimaPagina = pagina === totalPaginas - 1;

  const abrir = () => {
    setPagina(0);
    setVisivel(true);
    setAberto(true);
  };

  const irPara = (novaPagina) => {
    if (novaPagina === pagina || novaPagina < 0 || novaPagina >= totalPaginas)
      return;
    setVisivel(false);
    setTimeout(() => {
      setPagina(novaPagina);
      setVisivel(true);
    }, 130);
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        title="Ajuda sobre esta tela"
        aria-label="Ajuda sobre esta tela"
      >
        <IoMdHelpCircle color="red" size={26} />
      </button>

      {aberto && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setAberto(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 shrink-0">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <IoMdHelpCircle size={24} className="text-amber-500" />
                {titulo}
              </h3>
              <button
                onClick={() => setAberto(false)}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div
              className={`px-6 py-5 overflow-y-auto flex-1 transition-opacity duration-150 ${
                visivel ? "opacity-100" : "opacity-0"
              }`}
            >
              {atual?.tipo === "intro" && (
                <p className="text-gray-700 text-sm leading-relaxed">
                  {atual.texto}
                </p>
              )}

              {atual?.tipo === "passos" && (
                <ol className="space-y-4">
                  {atual.itens.map((passo, i) => {
                    const passoTitulo = typeof passo === "string" ? null : passo.titulo;
                    const passoTexto = typeof passo === "string" ? passo : passo.texto;
                    return (
                      <li key={i} className="flex gap-3">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        <div>
                          {passoTitulo && (
                            <p className="font-medium text-gray-800 text-sm">{passoTitulo}</p>
                          )}
                          <p className="text-sm text-gray-600 leading-relaxed">{passoTexto}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}

              {atual?.tipo === "secao" && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3 border-b border-gray-100 pb-2">
                    {atual.titulo}
                  </h4>
                  <dl className="space-y-3">
                    {atual.campos.map((campo) => (
                      <div key={campo.nome} className="flex flex-col">
                        <dt className="flex items-center gap-2">
                          <span className="font-medium text-gray-800 text-sm">
                            {campo.nome}
                          </span>
                          {campo.obrigatorio ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                              Obrigatório
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              Opcional
                            </span>
                          )}
                        </dt>
                        <dd className="text-sm text-gray-600 mt-0.5">
                          {campo.explicacao}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {atual?.tipo === "dicas" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">
                    Dicas
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                    {atual.itens.map((dica, i) => (
                      <li key={i}>{dica}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between shrink-0">
              <button
                onClick={() => irPara(pagina - 1)}
                disabled={primeiraPagina}
                className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  primeiraPagina
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <MdNavigateBefore size={20} />
                Anterior
              </button>

              {totalPaginas > 1 && (
                <div className="flex gap-1.5">
                  {paginas.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => irPara(i)}
                      aria-label={`Ir para o passo ${i + 1}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === pagina
                          ? "bg-amber-500"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}

              {ultimaPagina ? (
                <button
                  onClick={() => setAberto(false)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg transition-colors text-sm font-medium"
                >
                  Entendi
                </button>
              ) : (
                <button
                  onClick={() => irPara(pagina + 1)}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Próximo
                  <MdNavigateNext size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
